import React, { useState } from 'react';
import { Box, Button, Checkbox, Typography } from '@material-ui/core';
import Color from 'color';
import _ from 'lodash';
import { IGame, IGamePlayer, IDeath } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { ClassColors, TeamColors } from 'utilities/team_colors';
import { classImages } from 'utilities/ClassImages';
import { getDeathSeconds, formatGameTime, PlayerName } from './KillAdvantageGraph';
import { ChartTooltip, ChartTooltipState, HoverTarget } from './ChartTooltip';

const WIDTH = 900;
const HEIGHT = 340;
const PADDING = {top: 20, right: 20, bottom: 40, left: 64};

/** Running total for one character: `total` is their value after the event at `t` (seconds into the game). */
interface SeriesPoint {
  t: number;
  total: number;
  /** how much this death added */
  delta: number;
  death: IDeath;
}
type Series = Record<string, SeriesPoint[]>;

const formatDamage = (value: number) =>
  value >= 1000000 ? `${(value / 1000000).toFixed(1)}M` : value >= 1000 ? `${Math.round(value / 1000)}k` : `${Math.round(value)}`;

/**
 * One color per character based on their class. Characters sharing a class get the class color nudged
 * lighter/darker (and slightly rotated) so they can still be told apart.
 */
export const getCharacterColors = (players: IGamePlayer[]): Map<string, string> => {
  let colors = new Map<string, string>();
  let byClass = _.groupBy(_.sortBy(players.filter((p) => p.name), (p) => p.name), (p) => p.class || 'Unknown');
  Object.keys(byClass).forEach((className) => {
    let base = ClassColors(className as AocClasses) || '#9e9e9e';
    byClass[className].forEach((player, index) => {
      if(index == 0){
        colors.set(player.name as string, base);
        return;
      }
      // Each extra character of the same class rotates the hue 30 degrees (alternating direction, growing
      // with each extra one) and alternates darker/lighter, so the lines are easy to tell apart.
      // Lightness is clamped so a variant never ends up near black or white.
      let step = Math.ceil(index / 2);
      let odd = index % 2 == 1;
      let color = Color(base).rotate((odd ? 1 : -1) * 30 * step);
      let lightness = color.lightness() + (odd ? -15 : 12) * step;
      color = color.lightness(Math.min(80, Math.max(28, lightness))).saturationl(Math.min(100, color.saturationl() + 10));
      colors.set(player.name as string, color.hex());
    });
  });
  return colors;
};

/** Walks the game's deaths in time order, letting `onDeath` add to characters' running totals. */
const buildSeries = (game: IGame, onDeath: (death: IDeath, add: (name: string | undefined, value: number) => void) => void): Series => {
  let deaths = (game.deaths || [])
    .map((death) => ({death, t: getDeathSeconds(death, game)}))
    .filter(({t}) => t != null)
    .sort((a, b) => (a.t as number) - (b.t as number));
  let totals: Record<string, number> = {};
  let series: Series = {};
  for(let {death, t} of deaths){
    onDeath(death, (name, value) => {
      if(!name || value <= 0)
        return;
      totals[name] = (totals[name] || 0) + value;
      (series[name] = series[name] || []).push({t: t as number, total: totals[name], delta: value, death});
    });
  }
  return series;
};

/** Damage each character dealt, taken from the damage breakdown on every death. */
const getDamageSeries = (game: IGame) => buildSeries(game, (death, add) => {
  let byAttacker = _.groupBy(death.damage || [], (d) => d.name);
  Object.keys(byAttacker).forEach((name) => {
    if(name != death.victim?.name)
      add(name, byAttacker[name].reduce((sum, d) => sum + (d.value || 0), 0));
  });
});

/** Kills credited to each character (the death's killed_by). */
const getKillSeries = (game: IGame) => buildSeries(game, (death, add) => add(death.killed_by?.name, 1));

/** Deaths of each character. */
const getDeathSeries = (game: IGame) => buildSeries(game, (death, add) => add(death.victim?.name, 1));

interface TimelineProps {
  game: IGame;
  title: string;
  caption?: string;
  /** what the y axis counts, e.g. "damage" or "kills" (used in tooltips) */
  unit: string;
  series: Series;
  /** whole-number values (kills/deaths): integer axis ticks */
  integer?: boolean;
  formatValue?: (value: number) => string;
  /** what happened at this point, shown in the hover pop-out */
  describe: (name: string, point: SeriesPoint) => React.ReactNode;
}

/**
 * A running total over game time for the characters the user picks. Nothing is selected at first;
 * selected characters are drawn on the same chart in their own colors.
 */
const CharacterTimelineGraph: React.FC<TimelineProps> = ({game, title, caption, unit, series, integer, formatValue, describe}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [tooltip, setTooltip] = useState<ChartTooltipState | null>(null);
  const format = formatValue || ((value: number) => `${Math.round(value)}`);

  let players = (game.players || []).filter((p) => p.name);
  let colors = getCharacterColors(players);
  let teams = _.sortBy(_.uniq(players.map((p) => p.team == null ? -1 : p.team)), (t) => t == -1 ? 99 : t);

  let endTime = Math.max((game as any).totalTimeElapsed || 0, game.timeElapsed || 0, 60,
    ...Object.values(series).map((points) => points.length ? points[points.length - 1].t : 0));
  let maxTotal = Math.max(1, ...selected.map((name) => {
    let points = series[name] || [];
    return points.length ? points[points.length - 1].total : 0;
  }));

  let plotWidth = WIDTH - PADDING.left - PADDING.right;
  let plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const x = (t: number) => PADDING.left + (t / endTime) * plotWidth;
  const y = (total: number) => PADDING.top + plotHeight - (total / maxTotal) * plotHeight;

  let minuteStep = endTime <= 600 ? 1 : endTime <= 1800 ? 5 : 10;
  let xTicks: number[] = [];
  for(let m = 0; m * 60 <= endTime; m += minuteStep)
    xTicks.push(m * 60);
  let yTicks: number[] = [];
  if(integer){
    let step = Math.max(1, Math.ceil(maxTotal / 5));
    for(let v = 0; v <= maxTotal; v += step)
      yTicks.push(v);
  }else{
    yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * maxTotal);
  }

  const toggle = (name: string) =>
    setSelected((current) => current.includes(name) ? current.filter((n) => n != name) : [...current, name]);

  return (
    <Box p={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={1} style={{gap: 12}}>
        <Typography variant="subtitle1">{title}</Typography>
        {caption && <Typography variant="caption" style={{color: '#9e9e9e'}}>{caption}</Typography>}
      </Box>

      <Box display="flex" flexWrap="wrap" style={{gap: 24}} mb={1}>
        {teams.map((team) => (
          <Box key={team}>
            <Typography variant="body2" style={{color: team == -1 ? '#bdbdbd' : TeamColors[team], fontWeight: 600}}>
              {team == -1 ? 'No Team' : `Team ${team + 1}`}
            </Typography>
            {_.sortBy(players.filter((p) => (p.team == null ? -1 : p.team) == team), (p) => p.name).map((player) => {
              let name = player.name as string;
              let hasData = (series[name] || []).length > 0;
              return (
                <Box key={name} display="flex" alignItems="center" style={{gap: 6, opacity: hasData ? 1 : 0.5}}>
                  <Checkbox size="small" checked={selected.includes(name)} disabled={!hasData} onChange={() => toggle(name)} style={{padding: 2}} />
                  <span title="Line color" style={{display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: colors.get(name), border: '1px solid rgba(0,0,0,0.6)'}} />
                  <img src={classImages.getColorIcon(player.class as AocClasses)} title={player.class} style={{width: 20, height: 20}} />
                  <span style={{fontSize: '0.9em'}}>{name}</span>
                </Box>
              );
            })}
          </Box>
        ))}
        {selected.length > 0 &&
          <Box alignSelf="flex-start">
            <Button size="small" variant="outlined" onClick={() => setSelected([])}>Clear</Button>
          </Box>
        }
      </Box>

      {selected.length == 0 ?
        <Typography style={{opacity: 0.7, padding: '16px 0'}}>Select one or more characters to compare their {unit}.</Typography>
      :
        <Box position="relative">
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{width: '100%', height: 'auto', display: 'block'}}>
          {yTicks.map((v, index) => (
            <g key={`y${index}`}>
              <line x1={PADDING.left} x2={WIDTH - PADDING.right} y1={y(v)} y2={y(v)} stroke={index == 0 ? '#bdbdbd' : 'rgba(255,255,255,0.08)'} />
              <text x={PADDING.left - 8} y={y(v) + 4} textAnchor="end" fontSize={12} fill="#9e9e9e">{format(v)}</text>
            </g>
          ))}
          {xTicks.map((t) => (
            <g key={`x${t}`}>
              <line x1={x(t)} x2={x(t)} y1={PADDING.top} y2={HEIGHT - PADDING.bottom} stroke="rgba(255,255,255,0.06)" />
              <text x={x(t)} y={HEIGHT - PADDING.bottom + 18} textAnchor="middle" fontSize={12} fill="#9e9e9e">{formatGameTime(t)}</text>
            </g>
          ))}
          <text x={PADDING.left + plotWidth / 2} y={HEIGHT - 4} textAnchor="middle" fontSize={12} fill="#9e9e9e">Game time</text>

          {selected.map((name) => {
            let points = series[name] || [];
            let color = colors.get(name);
            let path = `M ${x(0)} ${y(0)}`;
            let previous = 0;
            for(let p of points){
              path += ` L ${x(p.t)} ${y(previous)} L ${x(p.t)} ${y(p.total)}`;
              previous = p.total;
            }
            path += ` L ${x(endTime)} ${y(previous)}`;
            return (
              <g key={name}>
                <path d={path} fill="none" stroke={color} strokeWidth={2} strokeOpacity={0.9} />
                {points.map((p, index) => {
                  let hovered = tooltip != null && tooltip.x == x(p.t) && tooltip.y == y(p.total);
                  return <circle key={index} cx={x(p.t)} cy={y(p.total)} r={hovered ? 5 : 3} fill={color} stroke={hovered ? '#fff' : 'none'} />;
                })}
              </g>
            );
          })}
          {selected.map((name) => (series[name] || []).map((p, index) => (
            <HoverTarget key={`${name}-hover${index}`} cx={x(p.t)} cy={y(p.total)} onLeave={() => setTooltip(null)} onEnter={() => setTooltip({
              x: x(p.t),
              y: y(p.total),
              content: (
                <div>
                  <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                    <span style={{display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: colors.get(name)}} />
                    <PlayerName game={game} name={name} />
                    <span style={{color: '#9e9e9e'}}>{formatGameTime(p.t)}</span>
                  </div>
                  <div>{describe(name, p)}</div>
                  <div style={{fontWeight: 600}}>Total: {p.total.toLocaleString()} {unit}</div>
                </div>
              )
            })} />
          )))}
        </svg>
        <ChartTooltip tooltip={tooltip} width={WIDTH} height={HEIGHT} />
        </Box>
      }
    </Box>
  );
}

interface Props {
  game: IGame;
}

export const CharacterDamageGraph: React.FC<Props> = ({game}) => (
  <CharacterTimelineGraph game={game} title="Damage over time" unit="damage" series={getDamageSeries(game)} formatValue={formatDamage}
    caption="Damage is taken from death breakdowns, so lines step up each time someone dies."
    describe={(name, point) => {
      // the abilities this character used on the victim, biggest first
      let abilities = _.sortBy((point.death.damage || []).filter((d) => d.name == name), (d) => -(d.value || 0)).slice(0, 3);
      return (
        <React.Fragment>
          <div>+{point.delta.toLocaleString()} damage to <PlayerName game={game} name={point.death.victim?.name} /> before they died</div>
          {abilities.map((ability) => (
            <div key={ability.ability} style={{color: '#bdbdbd', paddingLeft: 8}}>
              {ability.ability}: {(ability.value || 0).toLocaleString()}{ability.count ? ` (${ability.count}x)` : ''}
            </div>
          ))}
        </React.Fragment>
      );
    }} />
);

export const CharacterKillsGraph: React.FC<Props> = ({game}) => (
  <CharacterTimelineGraph game={game} title="Kills over time" unit="kills" series={getKillSeries(game)} integer
    caption="A kill is credited to the character the death lists as the killer."
    describe={(name, point) => <span>Killed <PlayerName game={game} name={point.death.victim?.name} /></span>} />
);

export const CharacterDeathsGraph: React.FC<Props> = ({game}) => (
  <CharacterTimelineGraph game={game} title="Deaths over time" unit="deaths" series={getDeathSeries(game)} integer
    describe={(name, point) => <span>Killed by <PlayerName game={game} name={point.death.killed_by?.name} /></span>} />
);
